import config from "../config/index.js";

/**
 * Lazy-loaded WalletConnect (v2) provider singleton.
 *
 * The SDK and its peer dependencies weigh ~400KB minified, so we only
 * pay that cost when the user actually clicks "Wallet Connect" in the
 * connect modal. Once initialised, the same provider is reused across
 * subsequent opens so an in-flight session isn't lost when the user
 * cancels and re-opens the modal.
 *
 * Returns the EIP-1193 compatible provider — which is exactly what the
 * rest of the app already expects (`getBrowserProvider()` /
 * `connectWallet()` work on it identically to a MetaMask injection).
 */
let providerPromise = null;

export function isWalletConnectConfigured() {
    return Boolean(config.walletConnectProjectId);
}

export async function getWalletConnectProvider() {
    if (!isWalletConnectConfigured()) {
        throw new Error(
            "WalletConnect is not configured. Set VITE_WALLETCONNECT_PROJECT_ID in your .env."
        );
    }
    if (!providerPromise) {
        providerPromise = (async () => {
            const { EthereumProvider } = await import("@walletconnect/ethereum-provider");
            const provider = await EthereumProvider.init({
                projectId: config.walletConnectProjectId,
                // Configured chain only — switching networks happens via
                // ensureChain() after the session is established.
                chains: [config.network.chainId],
                // CRITICAL: route read calls (eth_call, eth_estimateGas,
                // eth_gasPrice, eth_blockNumber …) to OUR configured RPC.
                // Without an rpcMap the SDK falls back to WalletConnect's
                // own blockchain API, which does not serve custom / local
                // / less-common chains — so every contract read and gas
                // estimate fails and transactions (borrow, early exit,
                // auto-compound) never even reach the wallet. Injected
                // wallets don't hit this because they carry their own RPC.
                rpcMap: {
                    [config.network.chainId]: config.network.rpcUrl
                },
                // We render our own QR inside the connect modal, so the
                // SDK shouldn't pop its own UI.
                showQrModal: false,
                // Method allowlist matches what the dapp actually uses.
                methods: [
                    "eth_sendTransaction",
                    "eth_sign",
                    "personal_sign",
                    "eth_signTypedData",
                    "eth_signTypedData_v4",
                    "wallet_switchEthereumChain",
                    "wallet_addEthereumChain"
                ],
                events: ["chainChanged", "accountsChanged"],
                metadata: {
                    name: "GILDer",
                    description: "Capital Protected Term Deposit Protocol",
                    url: typeof window !== "undefined" ? window.location.origin : "https://gilder.example",
                    icons: [
                        typeof window !== "undefined"
                            ? `${window.location.origin}/images/logo.png`
                            : "https://gilder.example/logo.png"
                    ]
                }
            });
            return provider;
        })().catch((err) => {
            // Reset so the next call can retry from scratch — sometimes
            // the very first init races with relay-server warmup.
            providerPromise = null;
            throw err;
        });
    }
    return providerPromise;
}

/**
 * Forcibly tears down the SDK singleton — used when the user cancels a
 * pending pairing so the next attempt starts clean instead of trying to
 * reuse a stale URI.
 */
export async function resetWalletConnectProvider() {
    if (!providerPromise) return;
    try {
        const provider = await providerPromise;
        if (provider?.session) {
            try {
                await provider.disconnect();
            } catch {
                /* ignore — session already dead */
            }
        }
    } finally {
        providerPromise = null;
    }
}
